"use client";

import { useAuth, useOrganization, useUser } from "@clerk/nextjs";
import { Container, Typography } from "@cappelendamm/design-system/react";
import { ArrowRight, Building, User } from "iconoir-react";
import { isFeideUser } from "@/auth/utils";
import {
  ContainerLink,
  type ContainerLinkProps,
} from "@/navigation/components/ContainerLink";

export const OrganizationBanner = ({
  message,
  backgroundColor,
  textColor,
}: {
  message?: ContainerLinkProps["link"];
  backgroundColor?: string;
  textColor?: string;
}) => {
  const { orgId } = useAuth();
  const { organization } = useOrganization();
  const { user, isLoaded } = useUser();

  if (!isLoaded || !user) return null;

  let displayName: string | null = null;

  if (isFeideUser(user)) {
    displayName =
      (user.publicMetadata?.activeSchoolName as string | null) ?? null;
  } else if (orgId) {
    // Non-Feide organization users may not always have organization context
    // immediately available during auth transitions.
    displayName = organization?.name ?? "Organisasjon";
  }

  if (!displayName) return null;

  return (
    <div
      className="bg-[#D1DBFF] text-black *:grid"
      style={{ backgroundColor, color: textColor }}
    >
      <Container className="flex w-[var(--cvr-container-width)] items-center gap-4 justify-self-center bg-transparent px-4 py-2 sm:grid sm:grid-cols-[1fr_auto_1fr] lg:px-0">
        <div className="flex min-w-0 items-center gap-2">
          <Building className="shrink-0" width={20} height={20} />
          <Typography as="span" variant="body-sm" className="truncate">
            {displayName}
          </Typography>
        </div>
        {message?.label ? (
          <div className="hidden min-w-0 justify-center sm:flex">
            <ContainerLink
              link={message}
              className="flex min-w-0 items-center gap-1.5 hover:underline"
            >
              <Typography
                as="span"
                variant="body-sm"
                className="truncate font-semibold"
              >
                {message.label}
              </Typography>
              {message.href ? (
                <ArrowRight className="shrink-0" width={16} height={16} />
              ) : null}
            </ContainerLink>
          </div>
        ) : null}
        <div className="hidden min-w-0 items-center justify-end gap-2 sm:col-start-3 sm:flex">
          <User className="shrink-0" width={20} height={20} />
          <Typography as="span" variant="body-sm" className="truncate">
            {user.firstName?.replace(/^\S/, (c) => c.toUpperCase())}
          </Typography>
        </div>
      </Container>
    </div>
  );
};
